import { useLocation, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import useScrollDetection from "../../../hooks/useScrollDetection";
import { useEffect, useState } from "react";
import { IoAlarmOutline } from "react-icons/io5";
import { IoMdCopy } from "react-icons/io";
import { useNotification } from "../../../components/context/NotificationContext";
import { BiChevronDown } from "react-icons/bi";
import SwipeConfirm from "../../../components/swipeComp/SwipeConfirm";
import {
  checkPaymentConsultation,
  checkPaymentTransactionJwt,
} from "../../../services/transactionApi";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import NotFound from "../../../assets/notFound.png";
import { TbReload } from "react-icons/tb";

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const SkeletonLoader = () => {
  return (
    <div className="py-12 space-y-6 animate-pulse">
      <div className="p-4 bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-200 rounded"></div>
          <div className="h-4 bg-slate-200 rounded w-40"></div>
        </div>
        <div className="h-8 bg-slate-200 rounded-lg w-24"></div>
      </div>

      <div className="flex flex-col w-full border border-slate-200 rounded-xl p-4 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-16 h-16 bg-slate-200 rounded"></div>
          <div className="h-6 bg-slate-200 rounded w-40"></div>
        </div>
        <hr />
        <div className="space-y-2">
          <div className="h-4 bg-slate-200 rounded w-48"></div>
          <div className="h-12 bg-slate-200 rounded w-full"></div>
        </div>
        <div className="flex items-center justify-between">
          <div className="h-5 bg-slate-200 rounded w-16"></div>
          <div className="h-6 bg-slate-200 rounded w-32"></div>
        </div>
      </div>

      <div className="h-12 border border-slate-200 bg-white rounded w-full flex items-center justify-center">
        <div className="h-5 bg-slate-200 rounded w-40"></div>
      </div>

      <div className="space-y-2">
        <div className="h-6 bg-slate-200 rounded w-48 mb-4"></div>
        {[1, 2, 3].map((item) => (
          <div key={item} className="py-3 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="h-5 bg-slate-200 rounded w-40"></div>
              <div className="w-5 h-5 bg-slate-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WaitingForPayment = () => {
  const navigate = useNavigate();
  const isScrolled = useScrollDetection(5);
  const { showNotification } = useNotification();
  const location = useLocation();
  const { type, detail } = location.state || "";
  const machineId = localStorage.getItem("MachineId");

  const [openConfirm, setOpenConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedTotal, setCopiedTotal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["detail-transaction-jwt", detail, machineId],
    queryFn: () =>
      checkPaymentTransactionJwt({
        inv_number: detail,
        uuid: machineId,
      }),
    enabled: !!(detail && machineId) && type !== 2,
    refetchOnWindowFocus: false,
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
  });

  const {
    data: checkPaymentConsultationData,
    isLoading: isLoadingConsultation,
    refetch: refetchConsultation,
  } = useQuery({
    queryKey: ["check-detail-consultation", detail],
    queryFn: () => checkPaymentConsultation(detail && detail.invoice),
    enabled: !!detail && type !== 1,
    refetchOnWindowFocus: false,
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (type === 1) {
      if (data && data.payment_due) {
        const calculateTimeLeft = () => {
          const now = moment();
          const dueDate = moment(data.payment_due, "YYYY-MM-DD HH:mm:ss");
          const diff = dueDate.diff(now);

          if (diff <= 0) {
            setTimeLeft(0);
            return;
          }

          setTimeLeft(diff);
        };

        calculateTimeLeft();

        const timer = setInterval(() => {
          calculateTimeLeft();
        }, 1000);

        return () => clearInterval(timer);
      }
    } else {
      if (detail) {
        const calculateTimeLeft = () => {
          const now = moment();
          const dueDate = moment(detail.payment_due, "YYYY-MM-DD HH:mm:ss");
          const diff = dueDate.diff(now);

          if (diff <= 0) {
            setTimeLeft(0);
            return;
          }

          setTimeLeft(diff);
        };

        calculateTimeLeft();

        const timer = setInterval(() => {
          calculateTimeLeft();
        }, 1000);

        return () => clearInterval(timer);
      }
    }
  }, [data, detail]);

  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) {
      return "00:00:00";
    }

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleCopyVA = () => {
    navigator.clipboard.writeText(
      type === 1 ? data?.va_numbers : detail?.va_numbers,
    );
    setCopied(true);
    showNotification("Nomor VA berhasil disalin!", "success");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyTotal = () => {
    navigator.clipboard.writeText(type === 1 ? data?.total : detail?.total);
    setCopiedTotal(true);
    showNotification("Total Pembayaran berhasil disalin!", "success");
    setTimeout(() => setCopiedTotal(false), 2000);
  };

  const toggleSection = (section) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    if (type === 1) {
      if (data && data !== undefined) {
        if (data.status_payment === 2) {
          navigate(`/purchase/checkout/success`, {
            state: { invoice_number: detail },
          });
        }

        const initialState = {};
        data.data?.forEach((_, index) => {
          initialState[`instruction-${index}`] = true;
        });
        setCollapsedSections(initialState);
      }
    } else if (type === 2) {
      if (checkPaymentConsultationData) {
        if (checkPaymentConsultationData.is_success === true) {
          const consultationId = detail?.invoice || "";
          if (consultationId) {
            sessionStorage.setItem(
              "waiting_consultation_id",
              consultationId.toString(),
            );
          }
          navigate(`/consultation/waiting`, {
            state: {
              consultationId,
            },
          });
        }
      }
    }
  }, [data, detail, checkPaymentConsultationData]);

  useEffect(() => {
    const instructions = type === 1 ? data?.data : detail?.data;

    if (instructions && instructions.length > 0) {
      const initialState = {};
      instructions.forEach((_, index) => {
        initialState[`instruction-${index}`] = true;
      });
      setCollapsedSections(initialState);
    }
  }, [data, detail, type]);

  useEffect(() => {
    if ((!isLoading || !isLoadingConsultation) && timeLeft === 0) {
      showNotification("Waktu pembayaran telah habis!", "error");
    }
  }, [timeLeft]);

  const handleManualRefetch = () => {
    if (type === 1) {
      refetch();
    } else {
      refetchConsultation();
    }
    showNotification("Status pembayaran telah diperbarui", "info");
  };

  return (
    <div className="bg-white relative">
      <div className="flex flex-row justify-center h-screen">
        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md py-4 px-3 lg:p-8 w-full h-fit">
            <div
              className={`bg-white fixed z-[9999] top-0 left-0 px-4 py-3 w-full flex flex-col items-center transition-all duration-300 ${
                isScrolled ? "shadow-md rounded-b-3xl" : ""
              }`}
            >
              <div className="flex flex-col w-full gap-4">
                <div className="w-full flex items-center gap-2 lg:hidden text-xl">
                  <div
                    onClick={() => {
                      if (
                        (!isLoading || !isLoadingConsultation) &&
                        timeLeft === 0
                      ) {
                        navigate(-1);
                      } else {
                        setOpenConfirm(true);
                      }
                    }}
                    className="p-1 hover:bg-gray-200 rounded-full"
                  >
                    <IoArrowBack className="text-gray-500" size={20} />
                  </div>
                  <p className="font-semibold text-slate-700">
                    Menunggu Pembayaran
                  </p>
                </div>
              </div>
            </div>

            {isLoading || isLoadingConsultation ? (
              <SkeletonLoader />
            ) : (
              <div className="py-12 space-y-6">
                <div className="text-center shadow-sm rounded-xl">
                  <div
                    className={`p-2 lg:px-4 lg:py-4 ${
                      timeLeft <= 300000
                        ? "bg-red-50 border-red-200"
                        : "bg-yellow-50 border-yellow-200"
                    } border rounded-lg flex items-center justify-between`}
                  >
                    <div className="flex items-center gap-2 text-md lg:text-base">
                      <IoAlarmOutline
                        size={16}
                        className={
                          timeLeft <= 300000 ? "text-error" : "text-yellow-600"
                        }
                      />
                      <p
                        className={`${
                          timeLeft <= 300000 ? "text-error" : "text-yellow-600"
                        } font-medium`}
                      >
                        Batas Waktu Pembayaran
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className={`${
                          timeLeft <= 300000 ? "bg-red-100" : "bg-yellow-100"
                        } px-3 py-1 rounded-lg`}
                      >
                        <p
                          className={`${
                            timeLeft <= 300000
                              ? "text-red-700"
                              : "text-yellow-700"
                          } font-bold text-md lg:text-lg`}
                        >
                          {formatTime(timeLeft)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {(!isLoading || !isLoadingConsultation) && timeLeft === 0 ? (
                  <div className="min-h-[calc(100vh-200px)] w-full flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center text-slate-700 px-4">
                      <img
                        src={NotFound}
                        alt="Not Found"
                        className="w-1/2 mb-3"
                      />
                      <h1 className="font-semibold text-lg text-center">
                        Waktu pembayaran telah habis
                      </h1>
                      <p className="text-center text-md mt-2">
                        Silahkan buat pesanan baru.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col w-full border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 lg:w-16 flex-shrink-0">
                          <img
                            src={type === 1 ? data?.logo : detail?.logo}
                            alt={type === 1 ? data?.name : detail?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h1 className="text-lg text-slate-700 font-semibold">
                          {type === 1 ? data?.name : detail?.name}
                        </h1>
                      </div>
                      <hr className="my-4" />
                      <div className="mb-4">
                        <p className="text-slate-700 mb-1">
                          Nomor Virtual Account
                        </p>
                        <div className="px-4 h-10 lg:h-12 lg:px-4 lg:py-2 rounded-lg bg-indigo-50 relative flex gap-2 items-center justify-center">
                          <p className="font-mono text-lg lg:text-lg font-semibold text-gray-800">
                            {type === 1 ? data?.va_numbers : detail?.va_numbers}
                          </p>
                          <button
                            onClick={handleCopyVA}
                            className="absolute right-4 top-1/2 -translate-y-1/2 translate-x-2 flex items-center gap-2 p-1 lg:px-3 lg:py-1 lg:border-2 text-primary rounded-full lg:rounded-lg hover:bg-indigo-100 transition-colors text-md cursor-pointer"
                          >
                            <IoMdCopy size={16} />
                            <p className="hidden lg:block">
                              {copied ? "Tersalin!" : "Salin"}
                            </p>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between font-semibold text-slate-700 lg:text-xl">
                        <p>Total</p>
                        <div className="flex items-center gap-1">
                          <p className="text-error tabular-nums">
                            Rp
                            {formatRupiah(
                              type === 1 ? data?.total : detail?.total,
                            )}
                          </p>
                          <button
                            onClick={handleCopyTotal}
                            className="flex items-center gap-2 p-1 lg:px-3 lg:py-1 lg:border-2 rounded-full lg:rounded-lg hover:bg-indigo-100 transition-colors text-md cursor-pointer"
                          >
                            <IoMdCopy size={16} />
                            <p className="hidden lg:block">
                              {copiedTotal ? "Tersalin!" : "Salin"}
                            </p>
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleManualRefetch}
                      disabled={isLoading || isLoadingConsultation}
                      className="h-[40px] border border-secondary text-secondary bg-white flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline transform duration-200 hover:scale-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading || isLoadingConsultation ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary"></div>
                          <p>Mengecek...</p>
                        </>
                      ) : (
                        <>
                          <p>Cek Status Pembayaran</p>
                          <TbReload size={18} />
                        </>
                      )}
                    </button>

                    <div>
                      <h3 className="text-base lg:text-xl font-semibold text-gray-800 mb-2">
                        Langkah Pembayaran
                      </h3>

                      <div className="divide-y divide-gray-200">
                        {type === 1
                          ? data &&
                            data.data?.map((instruction, index) => (
                              <div key={index} className="py-2">
                                <div
                                  className="flex justify-between items-center cursor-pointer"
                                  onClick={() =>
                                    toggleSection(`instruction-${index}`)
                                  }
                                >
                                  <h4 className="text-md lg:text-lg font-medium text-slate-700">
                                    {instruction.source}
                                  </h4>
                                  <BiChevronDown
                                    size={20}
                                    className={`transform transition-transform ${
                                      collapsedSections[`instruction-${index}`]
                                        ? "rotate-0"
                                        : "rotate-180"
                                    }`}
                                  />
                                </div>

                                {!collapsedSections[`instruction-${index}`] && (
                                  <div className="mt-2 pl-4">
                                    <ol className="space-y-1 list-none">
                                      {instruction.instruction.map(
                                        (step, stepIndex) => (
                                          <li
                                            key={stepIndex}
                                            className="text-slate-500 text-sm leading-relaxed"
                                          >
                                            {step}
                                          </li>
                                        ),
                                      )}
                                    </ol>
                                  </div>
                                )}
                              </div>
                            ))
                          : detail &&
                            detail.data?.map((instruction, index) => (
                              <div key={index} className="py-2">
                                <div
                                  className="flex justify-between items-center cursor-pointer"
                                  onClick={() =>
                                    toggleSection(`instruction-${index}`)
                                  }
                                >
                                  <h4 className="text-md lg:text-lg font-medium text-slate-700">
                                    {instruction.source}
                                  </h4>
                                  <BiChevronDown
                                    size={20}
                                    className={`transform transition-transform ${
                                      collapsedSections[`instruction-${index}`]
                                        ? "rotate-0"
                                        : "rotate-180"
                                    }`}
                                  />
                                </div>

                                {!collapsedSections[`instruction-${index}`] && (
                                  <div className="mt-2 pl-4">
                                    <ol className="space-y-1 list-none">
                                      {instruction.instruction.map(
                                        (step, stepIndex) => (
                                          <li
                                            key={stepIndex}
                                            className="text-slate-500 text-sm leading-relaxed"
                                          >
                                            {step}
                                          </li>
                                        ),
                                      )}
                                    </ol>
                                  </div>
                                )}
                              </div>
                            ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <SwipeConfirm
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={"Yakin Ingin Keluar?"}
        subTitle={"Keluar"}
        desc={
          type === 1
            ? "Jika kamu keluar dari halaman ini, kamu bisa lanjutkan pembayaran di daftar pesanan, pastikan masih dalam batas waktu pembayaran."
            : "Jika kamu keluar dari halaman ini, pesanan konsultasi akan dibatalkan dan pembayaran tidak dapat dilanjutkan."
        }
        handleConfirm={() => {
          navigate("/");
        }}
      />
    </div>
  );
};

export default WaitingForPayment;
